import { t } from "@lingui/macro";
import { useNavigation, NavigationProp } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  CalendarArrowIcon,
  LogOutIcon,
  TrashIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";
import { EventSignUpProcessSteps } from "@whoosh/features/src/Events/features/AddorEditSignUp";
import { mergeEligibleWindowCharges } from "@whoosh/features/src/Events/utils/mergeEligibleWindowCharges";
import { convertServerTimeToLocal } from "@whoosh/utilities/src/dates";

import { currentProfileAtom } from "../../../features/Club/atoms";
import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator/ClubStackNavigator";
import { useFlags } from "../../../utils/launchDarkly";
import { openEventSignUpModalAtom } from "../../EventDetail/atoms/actions/openEventSignUpModal/openEventSignUpModalAtom";
import { EventDetailEventHasFormAtom } from "../../EventDetail/atoms/eventForm";
import { useRemoveCurrentUserFromEvent } from "../../EventDetail/components/EventDetailActions/hooks/useRemoveCurrentUserFromEvent";
import { useDeleteEventSignUp } from "../../EventDetail/hooks/useDeleteEventSignUp";
import { EventSignUpRow } from "../../EventDetail/types";
import { EventSignupSummaryActions_event$key } from "./__generated__/EventSignupSummaryActions_event.graphql";
import { EventSignupSummaryActions_signUp$key } from "./__generated__/EventSignupSummaryActions_signUp.graphql";

type Props = {
  eventFragmentRef: EventSignupSummaryActions_event$key;
  signUpFragmentRef: EventSignupSummaryActions_signUp$key;
};

export const EventSignupSummaryActions = ({
  eventFragmentRef,
  signUpFragmentRef,
}: Props) => {
  const event = useFragment(
    EventSignupSummaryActionsEventFragment,
    eventFragmentRef
  );
  const signUp = useFragment(
    EventSignupSummaryActionsSignUpFragment,
    signUpFragmentRef
  );

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const [isLeaveEventModalOpen, setIsLeaveEventModalOpen] = useState(false);
  const [isRemoveMeFromEventModalOpen, setIsRemoveMeFromEventModalOpen] =
    useState(false);
  const currentProfile = useAtomValue(currentProfileAtom);
  const registrantId = currentProfile?.memberId;
  const openEventSignUpModal = useSetAtom(openEventSignUpModalAtom);
  const { newPatronEventBookingFlow } = useFlags();

  const accessStatusForClient = event.accessStatusForClient;
  const currentSignupWindow = signUp.signUpWindow;

  const handleDeleteSuccess = useCallback(() => {
    if (newPatronEventBookingFlow) {
      navigation.navigate("Home", { screen: "MainHome" });
    }
  }, [navigation, newPatronEventBookingFlow]);

  const [deleteEventSignUp] = useDeleteEventSignUp(handleDeleteSuccess);

  const handleDeleteRegistration = () => {
    if (signUp.id && registrantId) {
      deleteEventSignUp({ eventSignUpId: signUp.id }, registrantId);
      setIsLeaveEventModalOpen(false);
    }
  };

  const [removeCurrentPlayer] = useRemoveCurrentUserFromEvent(() =>
    setIsRemoveMeFromEventModalOpen(false)
  );

  const handleRemoveFromSignUp = () => {
    const input: EventSignUpRow = {
      id: signUp.id,
      waitlistIndex: signUp.waitlistIndex ?? 0,
      checkedIn: signUp.checkedIn,
      ownerClient: signUp.ownerClient?.id
        ? {
            type: signUp.ownerClient.memberOwnerId ? "MEMBER" : "CUSTOMER",
            id: signUp.ownerClient.id,
            firstName: "",
            lastName: "",
          }
        : undefined,
      registrations:
        signUp.registrations?.edges?.flatMap((e) =>
          e?.node?.id
            ? [
                {
                  id: e.node.id,
                  index: e.node.index === undefined ? null : e.node.index,
                  type: e.node.type === undefined ? null : e.node.type,
                  payingMemberId:
                    e.node.payingMember?.id ?? e.node.registrant?.id ?? "",
                  primaryRegistrationChargeTypeId:
                    e.node.primaryChargeType?.id ?? null,
                  registrant: {
                    ...e.node.registrant,
                    playerId: e.node.registrant?.id,
                    phoneNumber:
                      e.node.registrant?.phoneNumber?.nationalNumber ?? null,
                  },
                  formResponses: e.node.formResponses?.edges?.map((fr) => ({
                    questionConfigurationId:
                      fr?.node?.questionConfiguration.id ?? "",
                    stringValue: fr?.node?.stringValue,
                    booleanValue: fr?.node?.booleanValue,
                    transportationTypeId: fr?.node?.transportationType?.id,
                  })),
                },
              ]
            : []
        ) ?? [],
    };

    removeCurrentPlayer({ id: event.id, eventSignUpRow: input });
  };

  const isGroupEvent =
    event?.maxGuestCount !== 0 ||
    event?.maxMemberCount !== 1 ||
    event?.minMemberCount !== 1 ||
    event?.minGuestCount !== 0;

  const memberRegistrationCount =
    signUp?.registrations?.edges?.reduce(
      (acc, r) => (r?.node?.type === "MEMBER" ? acc + 1 : acc),
      0
    ) || 0;

  const nonMemberHoldRegistrationCount =
    signUp?.registrations?.edges?.reduce(
      (acc, r) =>
        r?.node?.type === "MEMBER" && r?.node?.registrant?.id ? acc + 1 : acc,
      0
    ) || 0;

  const registrationHasMultipleMembers = memberRegistrationCount > 1;

  const isClosed = ["CLOSED", "COMPLETE"].includes(accessStatusForClient || "");

  const currentDate = new Date();
  const currentDateTime = format(currentDate, "yyyy-MM-dd HH:mm:ss");

  const formattedCurrentSignupCancelDeadlineAt =
    currentSignupWindow?.cancelDeadlineAt
      ? format(
          convertServerTimeToLocal(currentSignupWindow.cancelDeadlineAt),
          "yyyy-MM-dd HH:mm:ss"
        )
      : null;

  const isInCancelWindowRange = formattedCurrentSignupCancelDeadlineAt
    ? currentDateTime < formattedCurrentSignupCancelDeadlineAt
    : true;

  const cancelRegistrationMessage = `${
    isGroupEvent
      ? t`Leaving this event will cancel the signup for the entire group. Are you sure you want to cancel the entire signup`
      : t`Are you sure you'd like to cancel your registration for ` +
        event.eventSeries.name
  }?${
    isClosed
      ? t`\nSign up is closed, and you won't be able to sign up again.`
      : ""
  }`;

  const openSignUpStep = (step: EventSignUpProcessSteps) => {
    openEventSignUpModal({ overrideStep: step });
  };

  const goToEventPage = () => {
    navigation.navigate("EventDetail", { eventID: event.id });
  };

  const isPayingMember = !!signUp?.registrations?.edges?.find(
    (r) => r?.node?.payingMember?.id === registrantId
  );

  const isPayingForOthers = !!signUp?.registrations?.edges?.some(
    (r) =>
      r?.node?.registrant?.id !== registrantId &&
      r?.node?.payingMember?.id === registrantId
  );

  const isMemberOwner = signUp?.ownerClient?.id === registrantId;
  const isCurrentProfileRegistered = signUp?.registrations?.edges?.find(
    (r) => r?.node?.registrant?.id === registrantId
  );

  const currentMemberCanLeaveEvent = nonMemberHoldRegistrationCount > 1;

  const isRemoveMeDisabled =
    !isInCancelWindowRange ||
    (isPayingForOthers && !currentMemberCanLeaveEvent);

  const showRemoveMeButton =
    isGroupEvent &&
    registrationHasMultipleMembers &&
    currentMemberCanLeaveEvent &&
    isCurrentProfileRegistered;

  const editSignupDisabled =
    !isInCancelWindowRange || (!isPayingMember && !isMemberOwner);
  const eventHasForm = useAtomValue(EventDetailEventHasFormAtom);
  // Charges are sourced from the windows the client is eligible for, merged and
  // de-duped across windows (backend already excludes HIDDEN for patrons).
  const eligibleCharges = mergeEligibleWindowCharges(
    event.clientEligibleSignUpWindows
  );
  const eventHasPayments = eligibleCharges.length > 0;
  const selectableChargeCount = eligibleCharges.filter(
    (prc) => prc.memberControl === "SELECTABLE"
  ).length;
  const showEditPaymentOption =
    !!selectableChargeCount && selectableChargeCount > 1;
  const isDraftSignUp = signUp.status === "DRAFT";
  const isPaidEventFee =
    event.prePaymentRequirement === "FULL" && signUp.status === "CONFIRMED";

  const showEditFormButton = !isDraftSignUp && eventHasForm;
  const showEditChargesButton =
    !isDraftSignUp &&
    eventHasPayments &&
    showEditPaymentOption &&
    !isPaidEventFee;
  const canCancelSignUp = signUp.canDelete === true;

  const showEditSignupButton =
    isGroupEvent ||
    eventHasPayments ||
    eventHasForm ||
    !!event.memberSignUpProxyAllowed;

  return (
    <>
      <VStack gap={3} testID="event-signup-summary-actions">
        {/* Hidden during the create flow (DRAFT). Leaving while draft is guarded by
            usePreventRemove on the summary screen (header X, back gesture, hardware
            back, and browser back all prompt Leave Signup + delete the draft). */}
        {!isDraftSignUp ? (
          <Button
            variant="secondary"
            width="100%"
            onPress={goToEventPage}
            leftIcon={<CalendarArrowIcon />}
            accessibilityLabel={t`Go to Event Page`}
            testID="event-signup-summary-go-to-event-button"
          >
            {t`Go to Event Page`}
          </Button>
        ) : null}

        {showEditSignupButton ? (
          <Button
            variant="outline"
            width="100%"
            onPress={() => openSignUpStep("PEOPLE")}
            isDisabled={editSignupDisabled}
            accessibilityLabel={t`Edit Signup`}
            testID="event-signup-summary-edit-signup-button"
          >
            {t`Edit Signup`}
          </Button>
        ) : null}

        {showEditFormButton ? (
          <Button
            variant="outline"
            width="100%"
            onPress={() => openSignUpStep("FORM")}
            isDisabled={editSignupDisabled}
            accessibilityLabel={t`Edit Form Answers`}
            testID="event-signup-summary-edit-form-button"
          >
            {t`Edit Form Answers`}
          </Button>
        ) : null}

        {showEditChargesButton ? (
          <Button
            variant="outline"
            width="100%"
            onPress={() => openSignUpStep("PRIMARY_CHARGE_SELECTION")}
            isDisabled={editSignupDisabled}
            accessibilityLabel={t`Edit Charges`}
            testID="event-signup-summary-edit-charges-button"
          >
            {t`Edit Charges`}
          </Button>
        ) : null}

        {canCancelSignUp && !isDraftSignUp ? (
          <Button
            accessibilityLabel={
              isGroupEvent ? t`Cancel Entire Signup` : t`Cancel Signup`
            }
            width="100%"
            variant="danger"
            leftIcon={<TrashIcon />}
            onPress={() => setIsLeaveEventModalOpen(true)}
            testID="event-signup-summary-cancel-signup-button"
          >
            {isGroupEvent ? t`Cancel Entire Signup` : t`Cancel Signup`}
          </Button>
        ) : null}

        {showRemoveMeButton ? (
          <Button
            accessibilityLabel={t`Remove Me from Signup`}
            width="100%"
            variant="secondary"
            leftIcon={<LogOutIcon />}
            onPress={() => setIsRemoveMeFromEventModalOpen(true)}
            isDisabled={isRemoveMeDisabled}
          >
            {t`Remove Me from Signup`}
          </Button>
        ) : null}
      </VStack>

      <PopUpModal
        isOpen={isLeaveEventModalOpen}
        upperTitle={t`Cancel Registration`}
        content={
          <VStack>
            <Text color="text-secondary">{cancelRegistrationMessage}</Text>
          </VStack>
        }
        confirmText={t`Cancel Signup`}
        cancelText={t`Cancel`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleDeleteRegistration();
          } else {
            setIsLeaveEventModalOpen(false);
          }
        }}
      />
      <PopUpModal
        isOpen={isRemoveMeFromEventModalOpen}
        upperTitle={t`Remove Me From Registration`}
        content={
          <VStack>
            <Text color="text-secondary">
              {t`Are you sure you'd like to remove yourself from ${event.eventSeries.name}?`}
            </Text>
          </VStack>
        }
        confirmText={t`Remove Me`}
        cancelText={t`Cancel`}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleRemoveFromSignUp();
          } else {
            setIsRemoveMeFromEventModalOpen(false);
          }
        }}
      />
    </>
  );
};

export const EventSignupSummaryActionsEventFragment = graphql`
  fragment EventSignupSummaryActions_event on Event {
    id
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    memberSignUpProxyAllowed
    accessStatusForClient(clientId: $memberId)
    prePaymentRequirement
    eventSeries {
      name
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      charges {
        edges {
          memberControl
          node {
            id
          }
        }
      }
    }
  }
`;

export const EventSignupSummaryActionsSignUpFragment = graphql`
  fragment EventSignupSummaryActions_signUp on EventSignUp {
    id
    canDelete
    status
    waitlistIndex
    checkedIn
    signUpWindow {
      cancelDeadlineAt
    }
    ownerClient {
      ... on Member {
        id
        memberOwnerId: id
      }
      ... on Customer {
        id
      }
    }
    registrations(first: 50) {
      edges {
        node {
          id
          type
          index
          ... on MemberEventRegistration {
            payingMember {
              id
            }
          }
          ... on GuestEventRegistration {
            payingMember {
              id
            }
          }
          primaryChargeType {
            id
          }
          registrant {
            id
            firstName
            lastName
            ... on Member {
              phoneNumber {
                nationalNumber
              }
              email
            }
            ... on Customer {
              phoneNumber {
                nationalNumber
              }
              email
            }
          }
          formResponses(first: 50) {
            edges {
              node {
                id
                booleanValue
                stringValue
                transportationType {
                  id
                }
                questionConfiguration {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;
